// /groups/:groupId/events/new — create an event, or a repeating one.
//
// ⚠ Submitting this form ANNOUNCES the event immediately: every member is
// notified before the request returns. The warning below is on the page for that
// reason, not as decoration.
//
// Unless it repeats. Setting "Repeats" branches the same form to the series
// endpoint, and a series announces NOTHING on create — each occurrence is
// materialized and announced later by cron (§5). So the two notices above the
// form are opposites and have to follow the mode, which is what `onModeChange`
// is for.

import { ApiClientError, createEvent, createSeries, fetchGroup } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { GroupRole, type GroupDetail } from '@msc/shared';
import { eventForm } from './eventForm.js';

export const eventNewView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  try {
    group = await fetchGroup(groupId);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load this group.'));
    return;
  }

  // The server gates this at role 50 regardless. Hiding the form is a courtesy,
  // never the guard.
  if (group.myRole < GroupRole.EVENTS_MANAGER) {
    main.replaceChildren(
      el('p', { class: 'form-error' }, 'You need to be an Events Manager to create events.'),
      el('p', {}, [el('a', { href: `/groups/${groupId}/events` }, 'Back to events')]),
    );
    return;
  }

  const oneOffNotice = el(
    'p',
    { class: 'notice' },
    'Creating this event notifies every member right away. There is no draft state.',
  );

  // The exact opposite of the notice above, which is the whole point of saying
  // it: a series creates no events and notifies nobody until an occurrence
  // materializes.
  const seriesNotice = el('p', { class: 'notice', hidden: true }, [
    'This creates the repeat rule only. No event exists and nobody is notified until ' +
      'an occurrence appears on the schedule below.',
  ]);

  // The full series form carries the same rule plus the series-level controls
  // (the upcoming-dates preview, "create next occurrence now", editing the rule
  // without touching occurrences already created). Worth a pointer, not a
  // detour — everything needed to START one is on this page.
  const seriesLink = el('p', { class: 'muted small', hidden: true }, [
    el('a', { href: `/groups/${groupId}/series/new` }, 'Use the full repeating-event form'),
    ' for the upcoming-dates preview and the rest of the series controls.',
  ]);

  const form = eventForm({
    groupTimeZone: group.timeZone,
    allowedEventTypes: group.allowedEventTypes,
    submitLabel: 'Create and announce',
    action: `/groups/${groupId}/events`,
    onSubmit: async (body) => {
      const created = await createEvent(groupId, body);
      navigate(`/events/${created.eventId}`, true);
    },
    recurrence: {
      // Not "and announce": the occurrences announce later, via cron Stage 0.
      seriesSubmitLabel: 'Create repeating event',
      seriesAction: `/groups/${groupId}/series`,
      onSubmitSeries: async (body) => {
        const created = await createSeries(groupId, body);
        navigate(`/series/${created.seriesId}`, true);
      },
      onModeChange: (repeating) => {
        oneOffNotice.hidden = repeating;
        seriesNotice.hidden = !repeating;
        seriesLink.hidden = !repeating;
      },
    },
  });

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/groups/${groupId}/events` }, 'Back to events')]),
    el('h1', {}, `New event in ${group.name}`),
    oneOffNotice,
    seriesNotice,
    seriesLink,
    el('section', { class: 'card' }, [form]),
  );
};
